import type { MouseEventHandler, MouseEvent } from 'react';
import { Assert } from '@team/assert';

/**
 * @example
 * ```tsx
type SomeData = {
  id: number;
  name: string;
};

type Props = {
  data: SomeData;
  onClick?: DataClickEventHandler<SomeData>;
};

const SomeDataComponent: FC<Props> = ({ data, onClick }) => {
  const handleClick = useDataClickEventHandler(data, onClick);

  return (
    <div role="button" onClick={handleClick}>
      {data.name}
    </div>
  );
};
```
 */
export function useDataClickEventHandler<T, E extends Element = Element>(
  data: T,
  handler?: DataClickEventHandler<T, E>
): Maybe<MouseEventHandler<E>> {
  if (Assert.isUndefined(handler)) {
    return undefined;
  }

  return (originalEvent) => {
    handler(data, originalEvent);
  };
}

export type DataClickEventHandler<T, E extends Element = Element> = (
  data: T,
  originalEvent: MouseEvent<E>
) => void;
